import React from 'react'
import PropTypes from 'prop-types'
import { useQuery } from 'react-query'
import isEmpty from 'lodash/isEmpty'
import { Card, Title, Notification } from '@offerzen/pinata-shame'

import { ApplicationStatus } from '@/CandidateExperience/constants/applicationStatus'
import { UserRole } from '@/CandidateExperience/helpers/constants'

import SidebarContentSkeleton from './SidebarContentSkeleton'

import ApplyForm from './ApplyForm'
import AppliedContent from './AppliedContent'
import MatchingRequirements from './MatchingRequirements'
import { getPositionMatchingRequirements } from './api'
import styles from './styles'

const SidebarContent = ({ theme, jobId, candidateSlug, candidateProfileId, application, companyName, handleApply }) => {
  const { data, isLoading, isError } = useQuery(['positionMatchingRequirements', jobId, candidateProfileId], () => {
    return getPositionMatchingRequirements({ positionId: jobId, candidateProfileId })
  })

  if (isError) {
    return (
      <Notification theme="error">
        <div role="alert">Something went wrong.</div>
      </Notification>
    )
  }

  if (isLoading || !data) {
    return <SidebarContentSkeleton />
  }

  const matchingRequirementsData = data.matchingRequirements
  const nonMatchingRequirements = Object.keys(matchingRequirementsData).filter(requirement => !matchingRequirementsData[requirement])

  const isMatchForJobRequirements = nonMatchingRequirements.length === 0
  const isApplicationPresent = !isEmpty(application)

  return (
    <div className={styles.container}>
      {!isMatchForJobRequirements && !isApplicationPresent && (
        <Card className={styles.card} theme={theme}>
          <MatchingRequirements nonMatchingRequirements={nonMatchingRequirements} />
        </Card>
      )}

      <Card className={styles.card} theme={theme}>
        <Choose>
          <When condition={isEmpty(application)}>
            <Title className={styles.applyHeading}>Apply for this job</Title>
            <ApplyForm handleApply={handleApply} companyName={companyName} isMatchForJobRequirements={isMatchForJobRequirements} />
          </When>

          <Otherwise>
            <AppliedContent
              applicationStatus={application.status}
              candidateSlug={candidateSlug}
              interviewRequestId={application.interviewRequest?.id}
              companyName={companyName}
            />
          </Otherwise>
        </Choose>
      </Card>

      {!isMatchForJobRequirements && isApplicationPresent && (
        <Card className={styles.card} theme={theme}>
          <MatchingRequirements nonMatchingRequirements={nonMatchingRequirements} />
        </Card>
      )}
    </div>
  )
}

SidebarContent.propTypes = {
  theme: PropTypes.string,
  jobId: PropTypes.string.isRequired,
  candidateProfileId: PropTypes.string.isRequired,
  candidateSlug: PropTypes.string.isRequired,
  handleApply: PropTypes.func.isRequired,
  application: PropTypes.shape({
    status: PropTypes.oneOf(Object.values(ApplicationStatus)),
    interviewRequest: PropTypes.shape({
      id: PropTypes.string,
    }),
  }),
  companyName: PropTypes.string.isRequired,
  loggedInUserRole: PropTypes.oneOf(Object.values(UserRole)),
}

export default SidebarContent
